/**
 * Status Utility Selectors
 *
 * Core utility selectors for application-wide status state (fetching, performing).
 * These selectors work with the Redux status state and are used by other selector systems.
 */

import { Map } from 'immutable';
import { DirectSelector } from '../../types/selectors';
import { createValidatedSelector } from '../../utils/createAppSelector';
import { RootState } from '../../types/redux';

/**
 * Base status selector - accesses the status state from Redux
 * Handles both Immutable and plain object state structures safely
 * Status values are numbers (counters) or undefined
 */
export const selectStatus: DirectSelector<Map<string, number | undefined>> = createValidatedSelector(
  [(state: RootState) => state],
  (state) => {
    if (!state || typeof state.get !== 'function') {
      return Map({
        isFetching: 0,
        isConsolidatingFetch: 0,
        isPerforming: 0,
        isConsolidatingPerform: 0
      });
    }

    const status = state.get('status');
    if (!status) {
      return Map({
        isFetching: 0,
        isConsolidatingFetch: 0,
        isPerforming: 0,
        isConsolidatingPerform: 0
      });
    }

    return status;
  }
);

/**
 * Selector for determining if the application is currently fetching data
 * Only considers actual API calls as "active fetching"
 */
export const selectIsFetching: DirectSelector<boolean> = createValidatedSelector(
  [selectStatus],
  (status) => {
    if (!status || typeof status.get !== 'function') {
      return false;
    }

    const isFetching = status.get('isFetching');

    // ONLY consider actual API calls as "active fetching"
    // Consolidation counters indicate pending processing, not active API calls
    return Boolean(isFetching);
  }
);

/**
 * Selector for determining if the application is currently performing operations
 * Includes both performing operations and consolidation processing
 */
export const selectIsPerforming: DirectSelector<boolean> = createValidatedSelector(
  [selectStatus],
  (status) => {
    if (!status || typeof status.get !== 'function') {
      return false;
    }

    const isPerforming = status.get('isPerforming');
    const isConsolidatingPerform = status.get('isConsolidatingPerform');

    return Boolean(isPerforming || isConsolidatingPerform);
  }
);

/**
 * Selector for determining if any API calls are currently active
 * Checks both fetching and performing operations
 */
export const selectIsAnyApiCallActive: DirectSelector<boolean> = createValidatedSelector(
  [selectStatus],
  (status) => {
    if (!status || typeof status.get !== 'function') {
      return false;
    }

    // Check if any API calls are currently active
    const isFetching = status.get('isFetching') || 0;
    const isPerforming = status.get('isPerforming') || 0;

    // ONLY consider actual API calls as "active"
    // Consolidation counters indicate pending processing, not active API calls
    return isFetching > 0 || isPerforming > 0;
  }
);
